<template>
  <v-dialog :value="value" width="450" persistent>
    <v-card>
      <v-card-title>Edit tariff</v-card-title>

      <v-card-text>
        
        <!-- -------------------------------------------------------------------------------- -->
        <div class="font-weight-bold dark_grey--text">Tariff</div>
        <v-row>
          <v-col>
            <v-select v-model="formData.tariff" :items="getFinalisedTariffs" item-text="name" :disabled="getIsProductLoading" label="Tariff" return-object outlined dense @change="tariffChanged" 
            :hide-details="tariffErrors.length===0" :error-messages="tariffErrors" :class="{ 'mb-n3': tariffErrors.length!==0 }"
            @input="$v.formData.tariff.$touch()" @blur="$v.formData.tariff.$touch()" />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-select v-model="formData.contractLength" :items="getContractLengths(formData.tariff.min_contract_length_months)" :disabled="getIsProductLoading || disableLengths" label="Contract length" outlined dense @change="lengthChanged"
            :hide-details="contractLengthErrors.length===0" :error-messages="contractLengthErrors" :class="{ 'mb-n3': contractLengthErrors.length!==0 }"
            @input="$v.formData.contractLength.$touch()" @blur="$v.formData.contractLength.$touch()" />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-select v-model="formData.promo" :items="getPromos(formData.tariff.promos,formData.contractLength)" :disabled="getIsProductLoading || disablePromos" item-text="name" label="Promo" return-object outlined dense hide-details/>
          </v-col>
        </v-row>
        <!-- -------------------------------------------------------------------------------- -->

        <!-- -------------------------------------------------------------------------------- -->
        <div class="font-weight-bold dark_grey--text">Trial</div>
        <v-row>
          <v-col>
            <v-select v-model="formData.freeTrialPeriod" :items="trialPeriods" label="Free trial period" :disabled="getIsProductLoading || disabledBeforeSold" outlined hide-details dense @change="changeTrialKind" />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col>
            <v-select v-model="formData.trialKind" :items="trialKinds" label="Trial kind" :disabled="getIsProductLoading || disabledBeforeSold || disabledIfNoTrialPeriod" outlined dense
            :hide-details="trialKindErrors.length===0" :error-messages="trialKindErrors" :class="{ 'mb-n3': trialKindErrors.length!==0 }"
            @input="$v.formData.trialKind.$touch()" @blur="$v.formData.trialKind.$touch()" />
          </v-col>
        </v-row>
        <!-- -------------------------------------------------------------------------------- -->

        <!-- -------------------------------------------------------------------------------- -->
        <div class="font-weight-bold dark_grey--text mb-4">Add Ons</div>
        <template v-for="(item,index) in formData.addons">
          <div :key="`addon_${index}`" class="addonRow">

            <div class="d-flex justify-space-between align-center">
              <div>{{item.name}}</div>

              <div class="d-flex justify-end"><div>+{{item.standard_price | formatCurrency}} (+{{(parseFloat(item.standard_price) * item.quantity) | formatCurrency}})</div></div>

              <template v-if="item.allow_quantity">
                <div class="inputField">
                  <v-text-field min="0" v-model="item.quantity" label="Quantity" type="number" :disabled="getIsProductLoading || disableAddon(item)" dense hide-details outlined />
                </div>
              </template>

              <template v-else>
                <div class="inputField">
                  <v-switch v-model="item.quantity" :disabled="getIsProductLoading" dense hide-details class="mt-n1" />
                </div>
              </template>
            </div>

          </div>
        </template>
        <!-- -------------------------------------------------------------------------------- -->

        <!-- -------------------------------------------------------------------------------- -->
        <div class="font-weight-bold dark_grey--text mb-4">Total</div>
        <div class="d-flex justify-space-between">
          <div>Total price exc. VAT</div>
          <div class="font-weight-bold">{{totals.excVat | formatCurrency}}</div>
        </div>

        <div class="d-flex justify-space-between">
          <div>VAT</div>
          <div class="font-weight-bold">{{totals.vat | formatCurrency}}</div>
        </div>

        <div class="d-flex justify-space-between">
          <div>Total price</div>
          <div class="font-weight-bold">{{totals.total | formatCurrency}}</div>
        </div>
        <!-- -------------------------------------------------------------------------------- -->

      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="primary" small @click="submit" :disabled="getIsProductLoading" :loading="getIsProductLoading">SAVE</v-btn>
        <v-btn color="primary" small outlined @click="close" :disabled="getIsProductLoading">CANCEL</v-btn>

      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapGetters,mapActions } from 'vuex';

  import { validationMixin } from "vuelidate";
  import { required } from "vuelidate/lib/validators";

  export default {

    mixins: [validationMixin],

    validations() {
      return !this.formData.freeTrialPeriod ? {
        formData: {
          tariff: {required},
          contractLength: {required},
        },
      } : {
        formData: {
          tariff: {required},
          contractLength: {required},
          trialKind: {required},
        },
      };
    },

    props: ['value'],

    watch: {

      value(open) {
        if(open){
          this.formData.tariff = this.getSelectedTariff(this.getCurrentProduct.tariff_id);
          this.formData.contractLength = this.getCurrentProduct.contract_length_months;
          this.formData.promo = this.getSelectedPromo(this.getCurrentProduct.tariff_id,this.getCurrentProduct.promo_id);
          this.formData.promo = this.formData.promo.id ? this.formData.promo : {};

          this.formData.freeTrialPeriod = this.getCurrentProduct.trial_period_length_days ? this.getCurrentProduct.trial_period_length_days : null;
          this.formData.trialKind = !this.formData.freeTrialPeriod ? null : this.getCurrentProduct.trial_kind;

          const selectedAddons = this.getSelectedAddon(this.getCurrentProduct);
          this.formData.addons = this.getAvailableAddons.map(e=>{
            const selectedAddon = selectedAddons.filter(a=>a.id===e.id)[0];
            const addons = {
              ...e,
              quantity: selectedAddon ? selectedAddon.quantity : 0
            };
            return addons;
          });
        }
      }

    },

    data() {
      return {
        formData: {
          tariff: {},
          contractLength: '',
          promo: {},
          freeTrialPeriod: null,
          trialKind: null,
          addons: [],
        },
        trialPeriods: [
          { text: "No Trial", value: 0 },
          { text: "7 Days", value: 7 },
        ],
        trialKinds: [
          { text: "Opt In", value: "OPT_IN" },
          { text: "Opt Out", value: "OPT_OUT", disabled: true },
        ],
      }
    },

    computed: {
      ...mapGetters('products',['getCurrentProduct']),
      ...mapGetters('globals',['hasUserGotRole']),
      ...mapGetters('productGeneral',['getSelectedTariff','getSelectedPromo','getSelectedAddon','getTariffs','getContractLengths','getPromos','getAvailableAddons','getIsProductLoading','getProductError']),
      
      tariffErrors() {
        const errors = [];
        if (!this.$v.formData.tariff.$dirty) return errors;
        !this.$v.formData.tariff.required && errors.push("Tariff is a required field");
        return errors;
      },
      
      contractLengthErrors() {
        const errors = [];
        if (!this.$v.formData.contractLength.$dirty) return errors;
        !this.$v.formData.contractLength.required && errors.push("Contract length is a required field");
        return errors;
      },
      
      trialKindErrors() {
        const errors = [];
        if (!this.formData.freeTrialPeriod || !this.$v.formData.trialKind.$dirty) return errors;
        !this.$v.formData.trialKind.required && errors.push("Trial kind is a required field");
        return errors;
      },

      disableLengths(){
        return !this.formData.tariff.id;
      },

      disablePromos(){
        return this.formData.contractLength === '';
      },

      disabledBeforeSold(){
        let isNotSold = this.getCurrentProduct.sold_date===null;
        let forApproval = this.getCurrentProduct.status==='For Approval';
        return !(isNotSold||forApproval);
      },

      disabledIfNoTrialPeriod(){
        return !this.formData.freeTrialPeriod;
      },

      totals(){
        const tariff = this.formData.tariff;
        const promo = this.formData.promo;
        const addons = this.formData.addons;

        let totalExcVat = (promo && promo.price) ? parseFloat(promo.price) : parseInt(tariff.standard_price);
        totalExcVat = addons.reduce((total,current)=>{
          total += current.quantity ? (parseFloat(current.standard_price) * current.quantity) : 0;
          return total;
        },totalExcVat);

        return {
          excVat: totalExcVat,
          vat: totalExcVat * 0.2,
          total: totalExcVat * 1.2
        };
      },

      getFinalisedTariffs(){
        return this.getTariffs.map(e=>{
          e.disabled = false;
          if(e.category==='voipmigration'){
            if(!this.hasUserGotRole('voip-migration-user')||(this.getCurrentProduct.status.toLowerCase()!=='upgrade' && this.getCurrentProduct.status.toLowerCase()!=='legacy upgrade')){
              e.disabled = true;
            }
          }
          return {...e};
        });
      },
    },

    methods: {
      ...mapActions('productGeneral',['updateProduct']),

      changeTrialKind(){
        if(!this.formData.freeTrialPeriod){ this.formData.trialKind = null; }else{
          this.formData.trialKind = 'OPT_IN';
        }
      },

      disableAddon(item){
        const statuses = ['warm','for approval','upgrade','legacy upgrade'];
        const prodStatus = this.getCurrentProduct.status.toLowerCase();
        return ( item.name==='ADDITIONAL DDIs' && (!this.hasUserGotRole('voip-migration-user') || (prodStatus!=='upgrade' && prodStatus!=='legacy upgrade')) ) 
        || ['Non-geographic number', 'Memorable number - SILVER for £5/month', 'Memorable number - GOLD for £10/month'].includes(item.name)
        || !statuses.includes(prodStatus)
      },

      tariffChanged(){
        this.formData.contractLength = {};
        this.formData.promo = {};
      },

      lengthChanged(){
        this.formData.promo = {};
      },

      async submit() {
        this.$v.$touch();
        if(!this.$v.$invalid) {
          const data = {
            product_id: this.getCurrentProduct.id,
            data: {
              tariff_id: this.formData.tariff.id,
              contract_length_months: this.formData.contractLength,
              promo_id: this.formData.promo.id ? this.formData.promo.id : null,
              addons: this.formData.addons.filter(e=>parseInt(e.quantity)>0 || e.quantity===true).map(e=>({addon_id: e.id, quantity: e.quantity})),
              trial_period_length_days: this.formData.freeTrialPeriod,
              trial_kind: this.formData.trialKind,
            }
          };
          await this.updateProduct(data);
          if(!this.getProductError){
            this.close();
            setTimeout(()=>{
              window.location.reload();
            },500);
          }
        }
      },

      close() {
        this.$v.$reset();
        this.formData = {tariff:{},contractLength:'',promo:{},freeTrialPeriod:'',trialKind:'',addons: []};
        this.$emit('input', false);
      },
    },

  }
</script>

<style lang="scss" scoped>

  .addonRow {
    height: 60px;
  }

  .inputField {
    width: 70px;
  }

</style>
